/**
 * =============================================================================
 * AuditPanel — admin-only audit-trail viewer UI (PR I-2)
 * =============================================================================
 *
 * The read-only front end for the ADR-023 audit-log read surface
 * (`GET /api/admin/audit-logs`). Display-safe by construction — it renders only
 * the server's already-redacted `AuditLogView` (never raw before/after blobs,
 * customer/participant PII, or free-text reasons), and it NEVER uses
 * `dangerouslySetInnerHTML`.
 *
 * Behaviour:
 *  - Lazy: nothing loads until the admin clicks "Load audit trail".
 *  - Server-side filters: action / entityType / entityId / date range (date-only
 *    inputs expand to start/end-of-day UTC; the backend does inclusive bounds).
 *  - Keyset "Load older" appends the next page via `nextCursor`.
 *  - Client-side actor filter over LOADED rows only, explicitly labelled — an
 *    admin with no visible row may still have acted (there is deliberately no
 *    server-side actor filter; see ADR-023).
 *  - Coverage is enforced per-mutation (BL-AUDIT-WRITE-RELIABILITY-001): an admin
 *    action either commits together with its audit row (atomic batch) or fails
 *    visibly (awaited). Entries exist only from the trail's introduction onward —
 *    surfaced in the coverage-note/empty-state copy.
 *
 * Self-contained + relocatable (like ConsentInspection): mounted globally today
 * (after ConsentInspection), re-homeable into the phase-2 Workbench IA later.
 * =============================================================================
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Box, Button, Flex, FlexItem, Form, FormGroup, Input, Panel, Select, Table, Text } from '@bigcommerce/big-design';
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  type AuditAction,
  type AuditEntityType,
  type AuditLogListQuery,
  type AuditLogView,
} from '@find-my-part/types';
import { useSession } from '../../hooks/useSession';
import { useAudit } from '../../hooks/useAudit';
import { formatRelativeTime } from '../../utils/formatters';
import { EmptyState } from '../common/EmptyState';

export interface AuditPanelProps {
  /** Optional test-id prefix (default "audit-panel"). Relocation-friendly. */
  testId?: string;
}

/**
 * Humanise an enum constant for display, e.g. QUESTION_APPROVED → "Question approved".
 * Acronyms are re-uppercased so labels match the server's entity labels on the
 * same row (the projection emits "RFI request", never "Rfi request").
 */
function humanise(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase();
  const label = lower.charAt(0).toUpperCase() + lower.slice(1);
  return label.replace(/\brfi\b/gi, 'RFI');
}

/**
 * Entity types whose ids the backend accepts for the `entityId` filter —
 * mirrors `AUDIT_ENTITY_ID_PATTERNS` in the worker's audit route (PARTICIPANT /
 * STORE_SETTINGS reject any id: their identifiers are sensitive / non-actionable
 * here). Guarded client-side so a doomed request never clears the loaded rows.
 */
const ENTITY_ID_SUPPORTED_TYPES: ReadonlySet<string> = new Set([
  'QUESTION',
  'REPLY',
  'RFI_REQUEST',
  'RFI_ITEM',
  'RFI_RESPONSE',
  'MEDIA_ATTACHMENT',
]);

const ACTION_OPTIONS = [
  { value: '', content: 'All actions' },
  ...AUDIT_ACTIONS.map((a) => ({ value: a, content: humanise(a) })),
];
const ENTITY_TYPE_OPTIONS = [
  { value: '', content: 'All entity types' },
  ...AUDIT_ENTITY_TYPES.map((e) => ({ value: e, content: humanise(e) })),
];

export function AuditPanel({ testId = 'audit-panel' }: AuditPanelProps): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();
  const { logs, hasMore, isLoading, isLoadingMore, error, hasLoaded, applyFilters, loadOlder } =
    useAudit(token ?? '');

  // Server-side filter form state.
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Inline validation for the entity-id/entity-type pairing (backend rejects an
  // id without a supported type — caught here so the doomed request never fires
  // and the loaded rows survive). Cleared when either field changes.
  const [entityIdError, setEntityIdError] = useState('');
  // Client-side (loaded-rows-only) actor filter.
  const [actorFilter, setActorFilter] = useState('');

  const buildQuery = useCallback((): AuditLogListQuery => {
    const query: AuditLogListQuery = {};
    if (action) query.action = action as AuditAction;
    if (entityType) query.entityType = entityType as AuditEntityType;
    const trimmedId = entityId.trim();
    if (trimmedId) query.entityId = trimmedId;
    // A date-only input expands to the full UTC day; the backend applies
    // inclusive bounds, so an end-of-day `toDate` includes the whole day.
    if (fromDate) query.fromDate = `${fromDate}T00:00:00.000Z`;
    if (toDate) query.toDate = `${toDate}T23:59:59.999Z`;
    return query;
  }, [action, entityType, entityId, fromDate, toDate]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Pre-flight the entity-id/entity-type pairing the backend enforces, so
      // the mistake is stopped at the field instead of a 400 wiping the table.
      const trimmedId = entityId.trim();
      if (trimmedId && !entityType) {
        setEntityIdError('Choose an entity type to filter by id.');
        return;
      }
      if (trimmedId && !ENTITY_ID_SUPPORTED_TYPES.has(entityType)) {
        setEntityIdError('Id filtering is not available for this entity type.');
        return;
      }
      setEntityIdError('');
      void applyFilters(buildQuery());
    },
    [applyFilters, buildQuery, entityId, entityType]
  );

  const handleClear = useCallback(() => {
    setAction('');
    setEntityType('');
    setEntityId('');
    setFromDate('');
    setToDate('');
    setEntityIdError('');
    setActorFilter('');
    void applyFilters({});
  }, [applyFilters]);

  // Client-side actor filter over the LOADED rows only.
  const displayedLogs = useMemo(() => {
    const needle = actorFilter.trim().toLowerCase();
    if (!needle) return logs;
    return logs.filter((l) => l.actor.toLowerCase().includes(needle));
  }, [logs, actorFilter]);

  // Not authenticated — the parent owns the login UX.
  if (!isAuthenticated || !token) {
    return null;
  }

  return (
    <Panel header="Audit trail" data-testid={`${testId}-card`}>
      <Text color="secondary" marginBottom="small">
        Recent admin actions in this store (who did what, and when), across questions, replies,
        RFIs, settings, and participant support. Display-safe only — no before/after content,
        customer emails, or free-text reasons are shown.
      </Text>

      <Box
        backgroundColor="secondary10"
        borderRadius="normal"
        padding="small"
        marginBottom="medium"
        data-testid={`${testId}-coverage-note`}
      >
        <Text marginBottom="none" color="secondary">
          Audit coverage is <strong>enforced</strong>: an admin action either commits together with
          its audit record, or the action fails visibly. Entries exist only from the audit
          trail&apos;s introduction onward, so older activity may predate the trail.
        </Text>
      </Box>

      <Form onSubmit={handleSubmit}>
        <Flex flexGap="16px" flexWrap="wrap" alignItems="flex-end">
          <FlexItem flexGrow={1} style={{ minWidth: '180px' }}>
            <Select
              label="Action"
              options={ACTION_OPTIONS}
              value={action}
              onOptionChange={(v) => setAction(v ?? '')}
              data-testid={`${testId}-filter-action`}
            />
          </FlexItem>
          <FlexItem flexGrow={1} style={{ minWidth: '180px' }}>
            <Select
              label="Entity type"
              options={ENTITY_TYPE_OPTIONS}
              value={entityType}
              onOptionChange={(v) => {
                setEntityType(v ?? '');
                setEntityIdError('');
              }}
              data-testid={`${testId}-filter-entity-type`}
            />
          </FlexItem>
          <FlexItem flexGrow={1} style={{ minWidth: '180px' }}>
            {/* FormGroup is required for BigDesign to render the Input's `error`. */}
            <FormGroup>
              <Input
                label="Entity id"
                placeholder="e.g. q_abc123def456"
                value={entityId}
                onChange={(e) => {
                  setEntityId(e.target.value);
                  setEntityIdError('');
                }}
                error={entityIdError || undefined}
                data-testid={`${testId}-filter-entity-id`}
              />
            </FormGroup>
          </FlexItem>
          <FlexItem style={{ minWidth: '150px' }}>
            <Input
              label="From date"
              description="Start of day, UTC"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid={`${testId}-filter-from-date`}
            />
          </FlexItem>
          <FlexItem style={{ minWidth: '150px' }}>
            <Input
              label="To date"
              description="End of day, UTC"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid={`${testId}-filter-to-date`}
            />
          </FlexItem>
        </Flex>

        <Flex marginTop="medium" flexGap="8px">
          <FlexItem>
            <Button type="submit" disabled={isLoading} isLoading={isLoading} data-testid={`${testId}-apply`}>
              {hasLoaded ? 'Apply filters' : 'Load audit trail'}
            </Button>
          </FlexItem>
          {hasLoaded && (
            <FlexItem>
              <Button type="button" variant="secondary" onClick={handleClear} disabled={isLoading} data-testid={`${testId}-clear`}>
                Clear
              </Button>
            </FlexItem>
          )}
        </Flex>
      </Form>

      {error && (
        <Box marginTop="medium">
          <Text color="danger" data-testid={`${testId}-error`}>
            {error}
          </Text>
        </Box>
      )}

      {hasLoaded && (
        <Box marginTop="medium">
          <FormGroup>
            <Input
              label="Filter loaded rows by actor"
              description="Filters the rows already loaded below only — an admin with no visible row may still have acted. There is no server-side actor search."
              placeholder="e.g. admin@store.com"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              data-testid={`${testId}-actor-filter`}
            />
          </FormGroup>
        </Box>
      )}

      {hasLoaded && displayedLogs.length > 0 && (
        <Box marginTop="medium" data-testid={`${testId}-results`}>
          {/* ADR-025 A-H (M13): the table scrolls INSIDE this container — the
              page body must never scroll sideways at supported widths. */}
          <Box data-testid="audit-table-scroll" style={{ overflowX: 'auto' }}>
            <Table
              columns={[
                {
                  header: 'When',
                  hash: 'when',
                  render: (log: AuditLogView) => (
                    <Flex flexDirection="column" flexGap="2px">
                      <Text bold marginBottom="none">
                        {log.createdAt ? formatRelativeTime(log.createdAt) : '—'}
                      </Text>
                      {log.createdAt && (
                        <Text color="secondary" marginBottom="none">
                          {new Date(log.createdAt).toLocaleString()}
                        </Text>
                      )}
                    </Flex>
                  ),
                },
                {
                  header: 'Action',
                  hash: 'action',
                  render: (log: AuditLogView) => (
                    <Text marginBottom="none">{log.displayAction ?? humanise(log.action)}</Text>
                  ),
                },
                {
                  header: 'Entity',
                  hash: 'entity',
                  render: (log: AuditLogView) => (
                    <Flex flexDirection="column" flexGap="2px">
                      <Text marginBottom="none">{log.entity.label}</Text>
                      {log.entity.id && (
                        <Text color="secondary" marginBottom="none">
                          {log.entity.id}
                        </Text>
                      )}
                    </Flex>
                  ),
                },
                {
                  header: 'Actor',
                  hash: 'actor',
                  render: (log: AuditLogView) => <Text marginBottom="none">{log.actor}</Text>,
                },
                {
                  header: 'Details',
                  hash: 'details',
                  render: (log: AuditLogView) => (
                    <Flex flexDirection="column" flexGap="2px">
                      {log.details.map((d, i) => (
                        <Text key={i} marginBottom="none">
                          <strong>{d.label}:</strong> {d.value}
                        </Text>
                      ))}
                      {log.reasonRecorded && <Badge label="Reason recorded" variant="secondary" />}
                      {log.details.length === 0 && !log.reasonRecorded && (
                        <Text color="secondary" marginBottom="none">
                          —
                        </Text>
                      )}
                    </Flex>
                  ),
                },
              ]}
              items={displayedLogs.map((l) => ({ ...l, id: l.logId }))}
            />
          </Box>
        </Box>
      )}

      {hasLoaded && logs.length > 0 && displayedLogs.length === 0 && (
        <EmptyState
          variant="note"
          testId={`${testId}-actor-empty`}
          body="No loaded rows match that actor. This filters loaded rows only — clear the actor filter or load older entries to widen the view."
        />
      )}

      {hasLoaded && logs.length === 0 && !isLoading && !error && (
        <EmptyState
          variant="note"
          testId={`${testId}-empty`}
          body="No audit entries match these filters. Entries exist only from the audit trail's introduction onward — try widening the filters or date range."
        />
      )}

      {/*
        "Load older" lives OUTSIDE the results block so it stays available when a
        client-side actor filter empties the visible rows (the older, unloaded
        pages may still contain matches). Disabled while a filter reload is in
        flight — that reload clears `nextCursor` (so `hasMore` is false here
        anyway), but the guard keeps the two loading paths from ever overlapping.
      */}
      {hasLoaded && hasMore && (
        <Flex marginTop="medium" justifyContent="center">
          <FlexItem>
            <Button
              variant="secondary"
              onClick={() => void loadOlder()}
              disabled={isLoadingMore || isLoading}
              isLoading={isLoadingMore}
              data-testid={`${testId}-load-older`}
            >
              Load older
            </Button>
          </FlexItem>
        </Flex>
      )}
    </Panel>
  );
}
